import { useMemo } from 'react';
import { useLocation, useMatches } from 'react-router';
import { useActiveProjectUuid } from '../../hooks/useActiveProject';
import site from './pages.json';

export type LookthroughAction = {
    label: string;
    does: string; // export | open_page | mail | upload (knowledge/_schema/page.json)
    page?: string;
    subject?: string;
};

export type LookthroughPage = {
    slug: string;
    name: string;
    order: number;
    icon: string;
    about?: string;
    assumptions?: string;
    tape_in_header?: boolean;
    controls?: { input: string; label: string }[];
    filters?: boolean;
    actions: LookthroughAction[];
};

/** Page facts, generated by deploy/seed.py from knowledge/presets/pages (spec D.2). */
export const LOOKTHROUGH = site as {
    contact: string;
    deals: Record<string, string>;
    pages: LookthroughPage[];
};

/** QueensGiant staff = organisation admins and developers (spec G9); clients are viewers (G8). */
export const isStaff = (role: string | undefined) => role === 'admin' || role === 'developer';

export const lookthroughPage = (slug: string | undefined) =>
    LOOKTHROUGH.pages.find((p) => p.slug === slug);

/**
 * Is the current URL a LOOKTHROUGH page? Decided from the URL slug alone, before any request, so
 * Phil's frame is up from the first render (spec D.2). Also carries the deal / as-of URL state
 * (?filters=, inputs) between pages.
 */
export const useLookthroughContext = () => {
    // ProjectLayout sits above the dashboard route, so read the ids from the full match list
    const { dashboardUuid } = useMatches().reduce<Record<string, string | undefined>>(
        (acc, m) => ({ ...acc, ...m.params }),
        {},
    );
    const { activeProjectUuid: projectUuid } = useActiveProjectUuid();
    const { search } = useLocation();
    const page = lookthroughPage(dashboardUuid);
    const context = useMemo(() => {
        const keep = new URLSearchParams();
        new URLSearchParams(search).forEach((v, k) => {
            if (k === 'filters' || k.startsWith('parameters')) keep.set(k, v);
        });
        const s = keep.toString();
        return s ? `?${s}` : '';
    }, [search]);
    return { active: !!page, page, pages: LOOKTHROUGH.pages, projectUuid, context };
};
